/**
 * Capacity calculation utilities for SOC gauges
 * Phase 5 Task 3: Implement capacity calculations
 */

/**
 * Calculate battery capacity information for SOC display
 * @param {number} soc - State of charge percentage (0-100)
 * @param {Array} realTimeData - Current real-time data array
 * @param {string} device - Device identifier (e.g., 'bat1', 'bat2')
 * @returns {Object|null} Capacity information or null if data unavailable
 */
export const calculateCapacity = (soc, realTimeData, device) => {
  if (!realTimeData || !Array.isArray(realTimeData) || soc == null || !device) {
    return null
  }

  // Find capacity data for the device
  const capacityItem = realTimeData.find(
    item => item.device === device && item.measurement === 'ampHrCapacity',
  )

  // Find voltage data for the device
  const voltageItem = realTimeData.find(
    item => item.device === device && item.measurement === 'volts',
  )

  // Early return if no capacity data available
  if (!capacityItem || capacityItem._value == null || capacityItem._value <= 0) {
    return null
  }

  // Use available voltage or default to 12V
  const voltage = voltageItem?._value ?? 12
  const totalCapacity = capacityItem._value

  // Calculate values
  const totalWh = totalCapacity * voltage
  const remainingAh = (soc / 100) * totalCapacity
  const remainingWh = remainingAh * voltage
  return {
    totalCapacity,
    totalWh,
    remainingAh,
    remainingWh,
    voltage,
    hasCapacityData: true,
  }
}

/**
 * Format capacity information for display
 * @param {Object} capacity - Capacity object from calculateCapacity
 * @returns {Object} Formatted strings for display
 */
export const formatCapacity = (capacity) => {
  if (!capacity || !capacity.hasCapacityData) {
    return {
      remaining: 'No capacity data',
      total: 'No capacity data',
      hasData: false,
    }
  }

  const { remainingAh, remainingWh, totalCapacity, totalWh } = capacity

  return {
    remaining: `${remainingAh.toFixed(0)} Ah / ${remainingWh.toFixed(0)} Wh`,
    total: `${totalCapacity.toFixed(0)} Ah / ${totalWh.toFixed(0)} Wh`,
    hasData: true,
  }
}

/**
 * Check if a device supports capacity calculations (batteries only)
 * @param {string} device - Device identifier
 * @returns {boolean} True if device supports capacity calculations
 */
export const supportsCapacity = (device) => {
  return device && device.startsWith('bat')
}
